import * as React from 'react';
import { Bug } from 'lucide-react';

import { cn } from '@common/lib/utils';
import { Button } from '@common/components/button';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import { BASE_URL, DEFAULT_BASE_URL, IS_DEBUG_API_OVERRIDE_ENABLED } from '@common/constants';
import {
  AUTO_SELECT_TIMEOUT_MS,
  DEFAULT_ENVIRONMENT_NAME,
  DEFAULT_FREEFORM_HINT,
  DEFAULT_PREVIEW_SUBDOMAIN,
  type ApiOverrideMode,
  type CatalogErrorState,
  type FreeformValidationError,
  type HostVerificationError,
  type PreviewEnvironment,
  type SubdomainValidationError,
  canClearOverride,
  canSaveFreeformDraft,
  canSavePreviewDraft,
  catalogErrorFromStatus,
  catalogErrorFromThrown,
  clearOverride,
  composedFreeformUrl,
  composedPreviewUrl,
  environmentDisplayName,
  environmentSublabel,
  extractPreviewSubdomain,
  filterPreviewEnvironments,
  normalizeSubdomain,
  parsePreviewEnvironments,
  previewSubdomainFromHost,
  readOverride,
  runAutoSelect,
  stripTrailingSlashes,
  subscribeApiNetworkFailure,
  validateFreeformBaseUrl,
  validateSubdomain,
  verifyApiHost,
  writeCachedEnvironmentName,
  writeFreeformOverride,
  writePreviewOverride,
} from '@utils/api-override';

const PREVIEW_ENVIRONMENTS_ENDPOINT = '/api/preview-environments';

const SUBDOMAIN_ERRORS: Record<SubdomainValidationError, string> = {
  empty: 'Subdomain cannot be empty',
  scheme_or_path: 'Enter only the subdomain (e.g. api or pr-6022-api)',
  contains_dot: 'Enter only the subdomain label, not a full host',
  invalid_characters: 'Use letters, numbers, and hyphens only',
};

const FREEFORM_ERRORS: Record<FreeformValidationError, string> = {
  empty: 'URL cannot be empty',
  invalid_url: 'Enter a valid URL (e.g. http://localhost:8000)',
  unsupported_scheme: 'Use http:// or https://',
  missing_host: 'Enter localhost, an IP, or a hostname',
  host_not_allowed: 'Use localhost, a private IP, or an oneteam.io, oneteam-preview.io, or flex-appeal.nl host',
};

const inputClassName = cn(
  'tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-py-2 tw-text-sm',
  'tw-text-foreground placeholder:tw-text-muted-foreground',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-custom/40',
);

const HOST_ERRORS: Record<HostVerificationError, Record<ApiOverrideMode, string>> = {
  unreachable: {
    preview_subdomain: 'Could not reach this API host. Check the subdomain and your connection.',
    freeform_url: 'Could not reach this API host. Check the URL and your connection.',
  },
  unhealthy: {
    preview_subdomain: 'This API host did not respond healthy. Pick another environment or fix the subdomain.',
    freeform_url: 'This API host did not respond healthy. Check the URL or try another host.',
  },
};

type CatalogStatus = 'idle' | 'loading' | 'ready' | 'error';

function catalogErrorMessage(error: CatalogErrorState): string {
  if (error.error === 'no_internet') return 'No connection. Try again.';
  if (error.error === 'unauthorized') return 'Catalog unauthorized.';
  if (error.error === 'http') {
    const base = `Could not load environments (${error.httpCode ?? 0}).`;
    return error.serverMessage ? `${base} ${error.serverMessage}` : base;
  }
  return 'Could not load environments.';
}

function initialSubdomain(defaultBaseUrl: string): string {
  const stored = readOverride();
  return stored.subdomain
    ?? extractPreviewSubdomain(defaultBaseUrl)
    ?? DEFAULT_PREVIEW_SUBDOMAIN;
}

function initialFreeform(): string {
  const stored = readOverride();
  if (stored.mode === 'freeform_url' && stored.baseUrl) return stored.baseUrl;
  return '';
}

async function loadCatalog(signal?: AbortSignal): Promise<PreviewEnvironment[]> {
  const response = await fetch(PREVIEW_ENVIRONMENTS_ENDPOINT, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const serverMessage = body && typeof body === 'object' && 'error' in body
      && typeof (body as { error?: unknown }).error === 'string'
      ? (body as { error: string }).error
      : undefined;
    const catalogError = catalogErrorFromStatus(response.status, serverMessage);
    const error = new Error(catalogErrorMessage(catalogError));
    (error as Error & { catalogError?: CatalogErrorState }).catalogError = catalogError;
    throw error;
  }

  return parsePreviewEnvironments(body);
}

export const DebugMenu = () => {
  const stored = readOverride();
  const activeUrl = stripTrailingSlashes(BASE_URL);
  const isOverridden = activeUrl !== stripTrailingSlashes(DEFAULT_BASE_URL);
  const suggestedSubdomain = previewSubdomainFromHost(
    typeof window !== 'undefined' ? window.location.hostname : '',
  );

  const [open, setOpen] = React.useState(false);
  const [draftMode, setDraftMode] = React.useState<ApiOverrideMode>(stored.mode ?? 'preview_subdomain');
  const [draftSubdomain, setDraftSubdomain] = React.useState(() => initialSubdomain(DEFAULT_BASE_URL));
  const [draftFreeform, setDraftFreeform] = React.useState(initialFreeform);
  const [hostError, setHostError] = React.useState<HostVerificationError | null>(null);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [environments, setEnvironments] = React.useState<PreviewEnvironment[]>([]);
  const [catalogStatus, setCatalogStatus] = React.useState<CatalogStatus>('idle');
  const [catalogError, setCatalogError] = React.useState<CatalogErrorState | null>(null);
  const [search, setSearch] = React.useState('');
  const draftEditedRef = React.useRef(false);

  const fetchEnvironments = React.useCallback(async () => {
    setCatalogStatus((current) => (current === 'ready' ? current : 'loading'));
    try {
      const next = await loadCatalog();
      setEnvironments(next);
      setCatalogStatus('ready');
      setCatalogError(null);
    } catch (error) {
      const known = error as Error & { catalogError?: CatalogErrorState };
      setCatalogError(known.catalogError ?? catalogErrorFromThrown(error));
      setCatalogStatus('error');
    }
  }, []);

  React.useEffect(() => {
    return subscribeApiNetworkFailure(() => setOpen(true));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    loadCatalog()
      .then((next) => {
        if (cancelled) return;
        setEnvironments(next);
        setCatalogStatus('ready');
        setCatalogError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const known = error as Error & { catalogError?: CatalogErrorState };
        setCatalogError(known.catalogError ?? catalogErrorFromThrown(error));
        setCatalogStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!suggestedSubdomain) return undefined;
    const controller = new AbortController();
    let cancelled = false;

    runAutoSelect({
      hostname: window.location.hostname,
      signal: controller.signal,
      timeoutMs: AUTO_SELECT_TIMEOUT_MS,
      verify: verifyApiHost,
    }).then((result) => {
      if (!cancelled && result === 'applied') window.location.reload();
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [suggestedSubdomain]);

  React.useEffect(() => {
    if (!open) return undefined;
    fetchEnvironments();
    return undefined;
  }, [open, fetchEnvironments]);

  React.useEffect(() => {
    if (draftEditedRef.current || !suggestedSubdomain) return;
    const current = readOverride();
    const isPinned = !!current.baseUrl && current.source !== 'auto';
    if (isPinned || current.mode === 'freeform_url') return;
    setDraftMode('preview_subdomain');
    setDraftSubdomain(suggestedSubdomain);
  }, [suggestedSubdomain]);

  const storedMode = stored.mode;
  const storedSource = stored.source;
  const storedSubdomain = stored.subdomain;
  const storedBaseUrl = stored.baseUrl;

  React.useEffect(() => {
    if (!environments.length) return;
    writeCachedEnvironmentName(activeUrl, environmentDisplayName({
      mode: storedMode,
      source: storedSource,
      subdomain: storedSubdomain,
      baseUrl: storedBaseUrl,
    }, environments));
  }, [activeUrl, environments, storedBaseUrl, storedMode, storedSource, storedSubdomain]);

  const environmentName = environmentDisplayName(stored, environments);

  const validationMessage = draftMode === 'preview_subdomain'
    ? (() => {
      const validation = validateSubdomain(normalizeSubdomain(draftSubdomain));
      return validation.valid ? null : SUBDOMAIN_ERRORS[validation.error];
    })()
    : (() => {
      const validation = validateFreeformBaseUrl(draftFreeform);
      return validation.valid ? null : FREEFORM_ERRORS[validation.error];
    })();

  const fieldError = validationMessage ?? (hostError ? HOST_ERRORS[hostError][draftMode] : null);
  const composedUrl = draftMode === 'preview_subdomain'
    ? composedPreviewUrl(draftSubdomain)
    : composedFreeformUrl(draftFreeform);
  const canSave = draftMode === 'preview_subdomain'
    ? canSavePreviewDraft(draftSubdomain, stored, DEFAULT_BASE_URL)
    : canSaveFreeformDraft(draftFreeform, stored, DEFAULT_BASE_URL);
  const canClear = canClearOverride(stored);
  const visibleEnvironments = filterPreviewEnvironments(environments, search);
  const normalizedDraft = normalizeSubdomain(draftSubdomain);

  const updateFreeform = (value: string) => {
    draftEditedRef.current = true;
    setHostError(null);
    if (!value.trim()) {
      setDraftFreeform('');
      setDraftMode('preview_subdomain');
      return;
    }
    setDraftMode('freeform_url');
    setDraftFreeform(value);
  };

  const selectEnvironment = (subdomain: string) => {
    if (isVerifying) return;
    draftEditedRef.current = true;
    setDraftMode('preview_subdomain');
    setDraftSubdomain(subdomain);
    setDraftFreeform('');
    setHostError(null);
  };

  const save = async () => {
    if (isVerifying || !canSave) return;
    setHostError(null);
    setIsVerifying(true);

    if (draftMode === 'preview_subdomain') {
      const validation = validateSubdomain(normalizeSubdomain(draftSubdomain));
      if (!validation.valid) {
        setIsVerifying(false);
        return;
      }
      const health = await verifyApiHost(composedPreviewUrl(validation.subdomain));
      if (health !== 'ok') {
        setHostError(health);
        setIsVerifying(false);
        return;
      }
      writePreviewOverride(validation.subdomain, 'manual');
      window.location.reload();
      return;
    }

    const validation = validateFreeformBaseUrl(draftFreeform);
    if (!validation.valid) {
      setIsVerifying(false);
      return;
    }
    const health = await verifyApiHost(validation.url);
    if (health !== 'ok') {
      setHostError(health);
      setIsVerifying(false);
      return;
    }
    writeFreeformOverride(validation.url, 'manual');
    window.location.reload();
  };

  const clear = () => {
    if (isVerifying || !canClear) return;
    clearOverride();
    window.location.reload();
  };

  if (!IS_DEBUG_API_OVERRIDE_ENABLED) return null;

  const title = (
    <span className="tw-flex tw-items-center tw-gap-2">
      <span
        className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-bg-orange-light tw-text-orange-custom"
      >
        <Bug size={14} />
      </span>
      API environment
    </span>
  );

  const footer = (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
      {(hostError && draftMode === 'preview_subdomain') && (
        <p className="tw-text-xs tw-text-red-link">{HOST_ERRORS[hostError].preview_subdomain}</p>
      )}
      {(hostError && draftMode === 'freeform_url') && (
        <p className="tw-text-xs tw-text-red-link">{HOST_ERRORS[hostError].freeform_url}</p>
      )}
      {isVerifying && (
        <p className="tw-text-xs tw-text-muted-foreground">Checking API host…</p>
      )}
      <div className="tw-flex tw-w-full tw-flex-wrap tw-gap-2 sm:tw-justify-end">
        <Button
          type="default"
          size="small"
          buttonType="button"
          icon="undo"
          disabled={!canClear || isVerifying}
          onClick={clear}
        >
          Clear override
        </Button>
        <Button
          type="primary"
          size="small"
          buttonType="button"
          isLoading={isVerifying}
          disabled={!canSave && !isVerifying}
          onClick={() => { save(); }}
        >
          Save
        </Button>
      </div>
    </div>
  );

  const content = (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4">
      <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
        <label className="tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-font-medium">
          Custom URL
          <input
            value={draftFreeform}
            disabled={isVerifying}
            placeholder={DEFAULT_FREEFORM_HINT}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => updateFreeform(event.target.value)}
            className={cn(
              inputClassName,
              'tw-font-mono',
              draftMode === 'freeform_url' && fieldError && 'tw-border-red-link',
            )}
          />
        </label>
        {fieldError && draftMode === 'freeform_url' && (
          <p className="tw-text-xs tw-text-red-link">{fieldError}</p>
        )}
        <p className="tw-font-mono tw-text-xs tw-text-muted-foreground">
          Effective URL: {composedUrl}
        </p>
      </div>

      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2">
        <p className="tw-shrink-0 tw-text-sm tw-font-medium">Preview environments</p>
        <input
          type="search"
          value={search}
          placeholder="Search environments"
          onChange={(event) => setSearch(event.target.value)}
          className={cn(inputClassName, 'tw-shrink-0')}
        />

        <div className="tw-min-h-[12rem] tw-flex-1 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-border
        tw-bg-white dark:tw-bg-card"
        >
          {(catalogStatus === 'loading' || catalogStatus === 'idle') && (
            <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
              Loading environments…
            </p>
          )}

          {catalogStatus === 'error' && catalogError && (
            <div className="tw-py-6 tw-text-center tw-text-sm tw-text-red-link">
              {catalogErrorMessage(catalogError)}{' '}
              <button type="button" onClick={fetchEnvironments} className="tw-underline">
                Retry
              </button>
            </div>
          )}

          {catalogStatus === 'ready' && visibleEnvironments.map((environment) => {
            const isSelected = draftMode === 'preview_subdomain'
              && environment.subdomain === normalizedDraft;
            const isActive = stripTrailingSlashes(environment.url) === activeUrl;

            return (
              <button
                type="button"
                key={environment.subdomain}
                disabled={isVerifying}
                aria-pressed={isSelected}
                onClick={() => selectEnvironment(environment.subdomain)}
                className={cn(
                  'tw-flex tw-w-full tw-items-start tw-gap-2 tw-border-b tw-border-border',
                  `tw-bg-white tw-p-3 tw-text-left tw-text-foreground last:tw-border-b-0 dark:tw-bg-card
                  dark:tw-text-card-foreground`,
                  'hover:tw-bg-gray-custom-100 dark:hover:tw-bg-dark-overlay',
                  isSelected && 'tw-bg-gray-custom-100 dark:tw-bg-dark-overlay tw-ring-1 tw-ring-inset tw-ring-orange-custom',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-full tw-border-2 tw-border-border',
                    'dark:tw-border-dark-border',
                    isSelected && 'tw-border-orange-custom tw-bg-orange-custom',
                  )}
                />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium">
                      {environment.label}
                    </span>
                    {isActive && (
                      <span className="tw-shrink-0 tw-text-xs tw-font-medium tw-text-orange-custom">
                        Active
                      </span>
                    )}
                  </span>
                  <span className="tw-mt-1 tw-block tw-truncate tw-text-xs tw-text-muted-foreground">
                    {environmentSublabel(environment)}
                  </span>
                </span>
              </button>
            );
          })}

          {catalogStatus === 'ready' && visibleEnvironments.length === 0 && (
            <p className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
              {environments.length === 0 ? 'No environments found.' : 'No environments match.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        aria-label={`API environment: ${environmentName}`}
        title={activeUrl}
        onClick={() => setOpen(true)}
        className={cn(
          'tw-fixed tw-z-[9999] tw-flex tw-h-10 tw-max-w-[min(240px,calc(100vw-2rem))]',
          'tw-bottom-[max(1rem,env(safe-area-inset-bottom))] tw-left-[max(1rem,env(safe-area-inset-left))]',
          'tw-items-center tw-gap-2 tw-rounded-full tw-bg-orange-custom tw-px-3',
          'tw-text-sm tw-font-medium tw-text-white tw-shadow-lg tw-transition-transform hover:tw-scale-105',
        )}
      >
        <Bug size={18} className="tw-shrink-0" />
        <span className="tw-truncate">{environmentName || DEFAULT_ENVIRONMENT_NAME}</span>
        {isOverridden && (
          <span
            className={cn(
              'tw-absolute tw-right-0 tw-top-0 tw-h-2.5 tw-w-2.5',
              'tw-rounded-full tw-bg-white tw-ring-2 tw-ring-orange-custom',
            )}
          />
        )}
      </button>

      <DialogWrapper
        open={open}
        onOpenChange={setOpen}
        size="md"
        title={title}
        description="Point the dashboard at a preview API. Saving reloads the page."
        className={
          '!tw-bg-white !tw-text-zinc-900 max-md:!tw-min-w-0 max-md:!tw-w-[calc(100vw-1.5rem)] '
          + 'max-md:!tw-max-w-[calc(100vw-1.5rem)] max-md:!tw-max-h-[calc(100dvh-1.5rem)]'
        }
        bodyClassName="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
        content={content}
        customFooter={footer}
      />
    </>
  );
};
